import { useQuery } from "@tanstack/react-query";
import { toCappedListResult } from "@genius-tracker/api-client";
import { apiGet } from "../lib/api.js";
import type { ListMembersResponse, MemberRole, MemberStatus } from "./types.js";

export interface MembersFilters {
  role?: MemberRole;
  status?: MemberStatus;
  q?: string;
  limit?: number;
}

const MEMBERS_KEY = ["admin", "members"] as const;

export function useMembers(filters: MembersFilters) {
  return useQuery<ListMembersResponse, Error>({
    queryKey: [...MEMBERS_KEY, filters],
    queryFn: ({ signal }) =>
      apiGet<ListMembersResponse>(
        "/v1/admin/members",
        {
          role: filters.role,
          status: filters.status,
          q: filters.q,
          limit: filters.limit ?? 100,
        },
        signal,
      ),
    staleTime: 15_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * `/v1/admin/members` caps `limit` server-side (max 100) and reports `total`
 * instead of a cursor, so the roster cannot be drained with `fetchAllPages`.
 * This gives it the same honest truncation signal via the shared
 * `@genius-tracker/api-client` helper instead of comparing `items.length`
 * and `total` ad hoc at each call site.
 */
export function membersTruncated(response: Pick<ListMembersResponse, "items" | "total">): boolean {
  return toCappedListResult(response).truncated;
}
